import { useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, FileText, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";

const POLITICAS = {
  "/privacidade": {
    icon: ShieldCheck,
    eyebrow: "Privacidade",
    titulo: "Política de Privacidade",
    atualizado: "Junho de 2026",
    intro:
      "A Maternnia respeita a sua privacidade. Esta política explica, de forma clara, quais dados coletamos, como os utilizamos e quais são os seus direitos, de acordo com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).",
    secoes: [
      {
        h: "Dados que coletamos",
        p: "Coletamos apenas o essencial para enviar conteúdos e conectar você a profissionais: nome, e-mail, WhatsApp (opcional), cidade, bairro (opcional), etapa da maternidade e assuntos de interesse. Não pedimos diagnósticos, exames, sintomas ou histórico médico.",
      },
      {
        h: "Como usamos seus dados",
        p: "Utilizamos seus dados para enviar materiais e conteúdos relacionados à sua etapa, melhorar a experiência da plataforma e, quando você solicitar, apresentar profissionais e serviços compatíveis com a sua necessidade.",
      },
      {
        h: "Compartilhamento",
        p: "Seus dados não são vendidos. Só compartilhamos informações com profissionais ou parceiros quando você manifesta interesse explícito, e sempre no limite necessário para o contato solicitado.",
      },
      {
        h: "Seus direitos",
        p: "Você pode, a qualquer momento, solicitar acesso, correção ou exclusão dos seus dados, além de revogar o consentimento para receber comunicações. Basta entrar em contato pelos nossos canais oficiais.",
      },
      {
        h: "Segurança",
        p: "Adotamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, perda ou uso indevido.",
      },
    ],
  },
  "/termos": {
    icon: FileText,
    eyebrow: "Termos",
    titulo: "Termos de Uso",
    atualizado: "Junho de 2026",
    intro:
      "Ao utilizar a Maternnia, você concorda com os termos descritos abaixo. Recomendamos a leitura atenta antes de utilizar a plataforma.",
    secoes: [
      {
        h: "Sobre a plataforma",
        p: "A Maternnia é uma plataforma de informação e conexão. Organizamos conteúdos por etapa da maternidade e apresentamos profissionais e serviços selecionados. Não somos um serviço de saúde nem prestamos atendimento médico.",
      },
      {
        h: "Caráter informativo",
        p: "Os conteúdos têm caráter educativo e não substituem consulta, diagnóstico ou acompanhamento individual com profissionais de saúde. Decisões sobre sua saúde devem ser tomadas com apoio profissional adequado.",
      },
      {
        h: "Profissionais e fornecedores",
        p: "A presença de um profissional ou serviço na plataforma não constitui recomendação médica nem garantia de resultado. Buscamos a curadoria da rede, mas a relação e a contratação ocorrem diretamente entre você e o profissional.",
      },
      {
        h: "Uso adequado",
        p: "Você se compromete a fornecer informações verdadeiras e a utilizar a plataforma de forma respeitosa, sem prejudicar outros usuários, profissionais ou a própria Maternnia.",
      },
      {
        h: "Alterações",
        p: "Podemos atualizar estes termos periodicamente. Mudanças relevantes serão comunicadas nos canais oficiais da plataforma.",
      },
    ],
  },
  "/politica-editorial": {
    icon: BookOpen,
    eyebrow: "Conteúdo",
    titulo: "Política Editorial",
    atualizado: "Junho de 2026",
    intro:
      "Acreditamos que informação de qualidade transforma a experiência da maternidade. Por isso, mantemos critérios claros na produção e revisão dos nossos conteúdos.",
    secoes: [
      {
        h: "Propósito",
        p: "Nossos conteúdos existem para trazer clareza em meio ao excesso de informação. Organizamos o que realmente importa em cada etapa — do planejamento ao pós-parto — com linguagem acolhedora e responsável.",
      },
      {
        h: "Revisão profissional",
        p: "Materiais de natureza técnica são produzidos pela equipe Maternnia e, sempre que possível, revisados por profissionais de saúde. Indicamos de forma transparente quando um conteúdo passou por revisão.",
      },
      {
        h: "Sem alarmismo",
        p: "Evitamos sensacionalismo, promessas e julgamentos. Respeitamos a diversidade de escolhas e contextos de cada família, sem impor um único caminho.",
      },
      {
        h: "Limites",
        p: "Conteúdos têm caráter educativo e não substituem avaliação individual. Em caso de dúvidas sobre sua saúde, procure sempre um profissional.",
      },
    ],
  },
};

const PoliticaPage = () => {
  const path = useLocation().pathname;
  const data = POLITICAS[path];
  if (!data) return <Navigate to="/" replace />;
  const Icon = data.icon;

  return (
    <main data-testid="politica-page" className="bg-white min-h-screen">
      <Seo title={data.titulo} path={path} description={data.intro} noindex />

      <section className="bg-maternnia-porcelana/60">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-12 md:pb-16">
          <Link to="/" data-testid="politica-voltar" className="inline-flex items-center gap-2 text-sm text-maternnia-taupe hover:text-maternnia-charcoal transition-colors mb-8">
            <ArrowLeft size={16} /> Voltar ao início
          </Link>
          <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center mb-6 shadow-sm">
            <Icon size={22} className="text-maternnia-eucalipto" />
          </div>
          <span className="text-sm font-semibold tracking-widest text-maternnia-eucalipto uppercase">{data.eyebrow}</span>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-maternnia-charcoal font-medium mt-3">{data.titulo}</h1>
          <p className="mt-3 text-sm text-maternnia-taupe/80 font-light">Atualizado em {data.atualizado}</p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <p className="text-lg md:text-xl text-maternnia-taupe leading-relaxed font-light">{data.intro}</p>
        <div className="mt-10 space-y-10">
          {data.secoes.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-serif text-2xl text-maternnia-charcoal font-medium">{s.h}</h2>
              <p className="mt-3 text-base text-maternnia-taupe leading-relaxed font-light">{s.p}</p>
            </motion.section>
          ))}
        </div>

        <div className="mt-14 bg-maternnia-nevoa/40 rounded-3xl p-7 md:p-8">
          <p className="text-sm text-maternnia-taupe leading-relaxed font-light">
            Dúvidas sobre esta política? Fale com a gente — estamos aqui para cuidar de você com transparência.
          </p>
          <Link to="/familias" data-testid="politica-cta" className="mt-5 inline-flex items-center gap-2 bg-maternnia-eucalipto text-white hover:bg-maternnia-floresta rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 hover:scale-[1.02]">
            Entrar em contato
          </Link>
        </div>
      </article>
    </main>
  );
};

export default PoliticaPage;
